// ============================================
// Zen-AI-Pentest Platform - Vulnerability Detail Page
// ============================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ShieldAlert,
  CheckCircle,
  Clock,
  Target,
  FileText,
  ExternalLink,
  Brain,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { vulnerabilityApi } from '@/services/api';
import type { Severity } from '@/types';

// ============================================
// Severity Config
// ============================================
const severityConfig: Record<Severity, { color: string; bgColor: string; label: string }> = {
  critical: { color: 'text-red-500', bgColor: 'bg-red-500/10', label: 'Critical' },
  high: { color: 'text-orange-500', bgColor: 'bg-orange-500/10', label: 'High' },
  medium: { color: 'text-yellow-500', bgColor: 'bg-yellow-500/10', label: 'Medium' },
  low: { color: 'text-blue-500', bgColor: 'bg-blue-500/10', label: 'Low' },
  info: { color: 'text-gray-500', bgColor: 'bg-gray-500/10', label: 'Info' },
};

// ============================================
// Main Vulnerability Detail Page
// ============================================
export function VulnerabilityDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState('details');

  const { data: vulnerability, isLoading } = useQuery({
    queryKey: ['vulnerability', id],
    queryFn: () => vulnerabilityApi.get(id!),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!vulnerability) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold">Vulnerability not found</h2>
        <Button asChild className="mt-4">
          <Link to="/vulnerabilities">Back to Vulnerabilities</Link>
        </Button>
      </div>
    );
  }

  const severity = severityConfig[vulnerability.severity];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/vulnerabilities">
              <ArrowLeft className="w-5 h-5" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{vulnerability.name}</h1>
            <div className="flex items-center gap-3 mt-2">
              <Badge className={`${severity.bgColor} ${severity.color}`}>
                <ShieldAlert className="w-3 h-3 mr-1" />
                {severity.label}
              </Badge>
              <span className="text-sm text-muted-foreground font-mono">
                {vulnerability.host}
                {vulnerability.port && `:${vulnerability.port}`}
              </span>
              <Badge variant="outline">{vulnerability.status}</Badge>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button variant="outline">
            <Brain className="w-4 h-4 mr-2" />
            AI Analysis
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="evidence">Evidence</TabsTrigger>
          <TabsTrigger value="remediation">Remediation</TabsTrigger>
          <TabsTrigger value="ai-analysis">AI Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Description</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">{vulnerability.description}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CVSS Score</span>
                  <span className="font-mono font-medium">
                    {vulnerability.cvss?.toFixed(1) || 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Host</span>
                  <span className="font-mono">{vulnerability.host}</span>
                </div>
                {vulnerability.port && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Port</span>
                    <span className="font-mono">{vulnerability.port}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Discovered</span>
                  <span>{new Date(vulnerability.createdAt).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant="outline">{vulnerability.status}</Badge>
                </div>
              </CardContent>
            </Card>
          </div>

          {vulnerability.cve && vulnerability.cve.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>CVE References</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {vulnerability.cve.map((cve) => (
                    <a
                      key={cve}
                      href={`https://cve.mitre.org/cgi-bin/cvename.cgi?name=${cve}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm hover:bg-accent transition-colors"
                    >
                      {cve}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {vulnerability.references && vulnerability.references.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>References</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {vulnerability.references.map((ref, index) => (
                    <li key={index}>
                      <a
                        href={ref}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline flex items-center gap-1"
                      >
                        {ref}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="evidence">
          <Card>
            <CardHeader>
              <CardTitle>Evidence</CardTitle>
              <CardDescription>Proof of vulnerability existence</CardDescription>
            </CardHeader>
            <CardContent>
              {vulnerability.evidence ? (
                <div className="space-y-4">
                  <div className="bg-muted p-4 rounded-lg">
                    <p className="text-sm font-medium mb-2">Type: {vulnerability.evidence.type}</p>
                    <pre className="text-sm overflow-auto">{vulnerability.evidence.data}</pre>
                  </div>
                  {vulnerability.evidence.request && (
                    <div>
                      <p className="text-sm font-medium mb-2">Request</p>
                      <pre className="bg-muted p-4 rounded-lg text-sm overflow-auto">
                        {vulnerability.evidence.request}
                      </pre>
                    </div>
                  )}
                  {vulnerability.evidence.response && (
                    <div>
                      <p className="text-sm font-medium mb-2">Response</p>
                      <pre className="bg-muted p-4 rounded-lg text-sm overflow-auto">
                        {vulnerability.evidence.response}
                      </pre>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No evidence available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="remediation">
          <Card>
            <CardHeader>
              <CardTitle>Remediation</CardTitle>
              <CardDescription>Steps to fix this vulnerability</CardDescription>
            </CardHeader>
            <CardContent>
              {vulnerability.remediation ? (
                <div className="prose dark:prose-invert max-w-none">
                  <p>{vulnerability.remediation}</p>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No remediation steps available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai-analysis">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                AI Analysis
              </CardTitle>
              <CardDescription>AI-powered vulnerability assessment</CardDescription>
            </CardHeader>
            <CardContent>
              {vulnerability.aiAnalysis ? (
                <div className="space-y-6">
                  <div>
                    <h4 className="font-medium mb-2">Summary</h4>
                    <p className="text-muted-foreground">{vulnerability.aiAnalysis.summary}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Impact</h4>
                    <p className="text-muted-foreground">{vulnerability.aiAnalysis.impact}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Exploitability</h4>
                    <p className="text-muted-foreground">{vulnerability.aiAnalysis.exploitability}</p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Recommendations</h4>
                    <ul className="list-disc list-inside space-y-1">
                      {vulnerability.aiAnalysis.recommendations.map((rec, index) => (
                        <li key={index} className="text-muted-foreground">
                          {rec}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Confidence:</span>
                    <span className="font-medium">
                      {(vulnerability.aiAnalysis.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">No AI analysis available yet</p>
                  <Button>Run AI Analysis</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
